import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { pathFor } from '../routes/paths';

/* Light, editorial footer matching the provided mock */
const FooterContainer = styled.footer`
  background: #fbf7f2;
  color: #2c2c2c;
  padding: 40px 0 70px;
  border-top: 1px solid #efe8e2;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 28px;
`;

/* Top strip: Left = contact card, Center = menu, Right = newsletter */
const TopStrip = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  gap: 28px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 26px;
  }
`;

/* Left column */
const Thumbs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: flex-start;
  max-width: 100%;
  @media (max-width: 980px) {
    justify-content: center;
  }
`;

const Thumb = styled.img`
  width: 92px;
  height: 112px;
  object-fit: cover;
  box-shadow: 0 10px 20px rgba(0,0,0,0.08);
`;

const SocialBlock = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 12px 16px;
  margin-top: 10px;

  @media (max-width: 980px) {
    grid-template-columns: auto;
    justify-items: center;
    text-align: center;
  }
`;

const FollowLabel = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 2px;
  color: #7a6f6a;
  text-transform: uppercase;
`;

const SocialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  @media (max-width: 980px) {
    justify-content: center;
  }
`;

const IconLink = styled.a`
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
`;

const IconImg = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

/* Center column with optional vertical rules */
const CenterBox = styled.div`
  display: grid;
  justify-items: center;
  padding: 0 34px;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: #e5ddd6;
  }
  &::before { left: 0; }
  &::after { right: 0; }
`;

/* Monogram removed per new structure */

const CenterNav = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  text-align: center;
`;

const CenterLink = styled.li`
  font-family: 'Tiro Bangla', serif;
  letter-spacing: 1.5px;
  color: #2c2c2c;
  text-transform: uppercase;
  font-size: 14px;
  margin: 6px 0;
  
  a { color: inherit; text-decoration: none; }
`;

/* Right column */
const ContactCard = styled.div`
  text-align: center;
  padding: 10px 0;
`;

const MeetTitle = styled.h4`
  font-family: 'Tiro Bangla', serif;
  text-transform: uppercase;
  color: #7c6a63;
  letter-spacing: 1.5px;
  font-size: 18px;
  margin: 0 0 8px;
`;

const MeetText = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  color: #7a7a7a;
  font-size: 14px;
  line-height: 1.7;
  margin: 0 0 12px;
`;

const ContactBtn = styled(motion.a)`
  display: inline-block;
  background: #4e1f28;
  color: white;
  text-decoration: none;
  padding: 10px 26px;
  border-radius: 2px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: none;
`;

/* Divider line before newsletter */
const Divider = styled.div`
  border-top: 1px solid #e9e0d9;
  margin: 24px 0 18px;
`;

/* Newsletter */
const NewsWrap = styled.div`
  text-align: center;
`;

const NewsTitle = styled.h5`
  font-family: 'Tiro Bangla', serif;
  text-transform: uppercase;
  letter-spacing: 1.8px;
  color: #7a6f6a;
  margin: 0 0 12px;
`;

const Footer = () => {
  const { lang } = useLanguage();
  return (
    <FooterContainer>
      <FooterContent>
        <TopStrip>
          {/* Left: contact card (Shall we meet?) */}
          <ContactCard>
            <MeetTitle>{lang === 'fr' ? 'On se rencontre ?' : 'Shall we meet?'}</MeetTitle>
            <MeetText>
              {lang === 'fr'
                ? "Écrivez‑moi et prenons le temps d’échanger sur votre projet et vos idées."
                : "Write to me and let's take the time to discuss your project and your ideas."}
            </MeetText>
            <ContactBtn href={pathFor('contact', lang)} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
              {lang === 'fr' ? 'Me contacter' : 'Contact me'}
            </ContactBtn>
          </ContactCard>

          {/* Center: menu */}
          <CenterBox>
            <CenterNav>
              <CenterLink><a href={pathFor('home', lang)}>{lang === 'fr' ? 'Accueil' : 'Home'}</a></CenterLink>
              <CenterLink><a href={pathFor('experience', lang)}>{lang === 'fr' ? 'Expérience' : 'Experience'}</a></CenterLink>
              <CenterLink><a href={pathFor('portfolio', lang)}>Portfolio</a></CenterLink>
              <CenterLink><a href={pathFor('about', lang)}>{lang === 'fr' ? 'À propos' : 'About'}</a></CenterLink>
              <CenterLink><a href={pathFor('videographer', lang)}>Film</a></CenterLink>
              <CenterLink><a href={pathFor('contact', lang)}>{lang === 'fr' ? 'Contact' : 'Contact'}</a></CenterLink>
            </CenterNav>
          </CenterBox>

          {/* Right: newsletter */}
          <NewsWrap>
            <NewsTitle>{lang === 'fr' ? 'S’inscrire à la newsletter' : 'Subscribe to the newsletter'}</NewsTitle>
            <iframe
              title="Fotostudio newsletter form"
              src="https://www.fotostudio.io/lead_forms/12559"
              width="100%"
              height="350"
              frameBorder="0"
              allowFullScreen
              loading="lazy"
              style={{ border: 0 }}
            />
          </NewsWrap>
        </TopStrip>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
